import {useEffect, useRef, useState} from 'react';
import styled from 'styled-components';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {faCheck} from '@fortawesome/pro-solid-svg-icons';
import {copyIconWhite, viewImageCheck2} from '@/utils/icons';
import {Col, Spin} from 'antd';
import {SkeletonRingLoader} from '@/components/common-components';
import {useStore} from '@/store/root-store';
import {observer} from 'mobx-react';
import {insertImage} from '@/utils/editor-scripts';
import {LoadingOutlined} from '@ant-design/icons';


export const ImageComponent = observer(({data, idx, setImagesList, onChange, store, historyCopyStatus, setShowSelectedHistoryImage, setShowHistoryImage, rootElement, setImagesAddedCount, imagesAddedCount}) => {
  const ref = useRef(null);
  const observer = useRef(null);
  const antIcon = <LoadingOutlined style={{fontSize: 15, color: '#ffffff'}} spin />;
  const [elemIdx, setElemIdx] = useState(0);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [imagesLoadedCount, setImagesLoadedCount] = useState(0);

  const {
    contentOptimizer: {
      currentPage: {
        setShowAddHeroCheck,
        setShowAddToArticleCheck,
        setShowCopiedCheck,
        showAddHeroCheck,
        showAddToArticleCheck,
        showCopiedCheck,
        updateHeroImage,
        isHeroImageLoading,
      },
      aiOutline: {
        templatesHistory,
      },
    },
  } = useStore('');

  useEffect(() => {
    const currentElement = ref.current;
    if (currentElement) {
      observer.current = new IntersectionObserver(
        entries => {
          entries.forEach(entry => {
            if (entry.intersectionRatio > 0.2) {
              observer.current.unobserve(currentElement);
              const imagesLoading = templatesHistory?.slice(imagesAddedCount, imagesAddedCount+20).map(item => {
                return {...item, loading: true};
              }) || [];
              setImagesList(imageList => {
                const images = imagesLoading?.filter(item => !imageList?.map(image => image.id).includes(item?.id)) || [];
                setImagesAddedCount(images?.length + imageList.length);
                return [...imageList, ...images];
              });
            }
          });
        },
        {
          root: rootElement.current,
          threshold: [0, 0.5, 1],
        },
      );

      observer.current.observe(currentElement);
    }

    return () => {
      observer.current.unobserve(currentElement);
    };
  }, [ref]);

  const handleHistoryImageCopyClick = link => {
    if (navigator.clipboard) {
      navigator.clipboard.writeText(link);
    } else {
      // Fallback for browsers that don't support the Clipboard API
      document.execCommand('copy');
    }
    setShowCopiedCheck(true);
  };

  const onImageUpload = (imageUrl, alt) => {
    const newEditorState = insertImage(store.currentEditorState, imageUrl, alt);
    onChange(newEditorState);
    setShowAddToArticleCheck(true);
  };

  const onAddImage = item => {
    onImageUpload(item?.result, item.inputs[0]?.fieldValue);
  };

  const selectedHistoryImageAdd = (index: any) => {
    setShowHistoryImage(true);
    setShowSelectedHistoryImage(index);
  };

  const imgOnLoad = id => {
    setImagesList(imageList => {
      for (let i = 0; i < imageList.length; i++) {
        if (imageList[i].id === id) {
          imageList[i].loading = false;
          break; // Stop the loop once the item is found and updated
        }
      }
      setImagesLoadedCount(imageList.filter(item => item.loading == false).length);
      return imageList;
    });
  };

  const onMouseLeave = () => {
    setShowAddToArticleCheck(false);
    setShowAddHeroCheck(false);
    setShowCopiedCheck(false);
  };

  return <Col ref={ref} key={idx} className='gutter-row' span={8}>
    <div onMouseLeave={() => onMouseLeave()} onMouseOver={() => setElemIdx(idx)}>
      {data?.loading && <SkeletonRingLoader/>}
      <ColCardContainer
        hide={data?.loading}>
        <ColCardImage
          hide={data?.loading}
          src={`${data?.result}`}
          alt={String(data?.inputs[0]?.fieldValue)}
          onLoad={() => imgOnLoad(data?.id)}
        />
        <div className='overlay'></div>
        <button className='add-as-hero-btn' onClick={e => {
          e.stopPropagation();
          !isHeroImageLoading && updateHeroImage(data?.result, data?.inputs[0]?.fieldValue || '');
        }}><div>{isHeroImageLoading ? <Spin indicator={antIcon} /> : showAddHeroCheck && elemIdx == idx ? <FontAwesomeIcon icon={faCheck}/> : '+ As Hero'}</div></button>
        <button className='copy-button' onClick={e => {
          e.stopPropagation();
          handleHistoryImageCopyClick(data?.result);
        }}><div style={{display: 'flex', alignItems: 'center'}}>{showCopiedCheck && elemIdx == idx ? <FontAwesomeIcon icon={faCheck}/> : <>{copyIconWhite()}<div style={{marginLeft: '3px'}}>{historyCopyStatus}</div></>}</div></button>
        <button className='add-to-atricle-btn' onClick={e => {
          e.stopPropagation();
          onAddImage(data);
        }}><div>{showAddToArticleCheck && elemIdx == idx ? <FontAwesomeIcon icon={faCheck}/> : '+ To Article'}</div></button>
        <button className='view-button' onClick={e => {
          e.stopPropagation();
          selectedHistoryImageAdd(idx);
        }}><div style={{display: 'flex', alignItems: 'center'}}>{viewImageCheck2()}<div style={{marginLeft: '3px'}}>View</div></div></button>
      </ColCardContainer>
      <div style={{marginTop: '6px'}}>{data?.inputs[0]?.fieldValue}</div>
    </div>
  </Col>;
});


const ColCardImage = styled.img<{hide?: boolean}>`
  display: ${p => p?.hide ? 'none' : ''};
  width: 100%;
  border-radius: 12px;
  cursor: pointer;
  background-color: #000000;
`;

const ColCardContainer = styled.div<{hide?: boolean}>`
  display: ${p => p?.hide ? 'none' : ''};
  position: relative;
  cursor: pointer;

  .overlay {
    display: ${p => p?.hide ? 'none' : ''};
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, 0);
  }

  /* Style for the button Article Btn */
  .add-to-atricle-btn {
    display: ${p => p?.hide ? 'none' : ''};
    position: absolute;
    top: 28%;
    left: 74%;
    transform: translate(-50%, -50%);
    background-color: rgba(255, 255, 255, 0.3);
    color: white;
    border: none;
    border-radius: 4px;
    cursor: pointer;
    opacity: 0; /* Initially hidden */
    width: 80px;
    height: 28px;
    font-size: 10px;
    line-height: 10px;
    font-weight: 400;
    display: flex;
    justify-content: center;
    align-items: center;
  }

  /* Black transparent overlay */
  &::before {
    display: ${p => p?.hide ? 'none' : ''};
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    border-radius: 12px;
    background-color: rgba(0, 0, 0, 0);
  }

  /* Show the button on hover */
  &:hover .add-to-atricle-btn {
    display: ${p => p?.hide ? 'none' : ''};
    opacity: 1;
  }

  .add-to-atricle-btn:hover {
    display: ${p => p?.hide ? 'none' : ''};
    background-color: rgba(255, 255, 255, 0.2);
  }

  &:hover::before {
    display: ${p => p?.hide ? 'none' : ''};
    background-color: rgba(0, 0, 0, 0.8);
  }

  /* Style for the button Copy Btn */
  .copy-button {
    display: ${p => p?.hide ? 'none' : ''};
    position: absolute;
    top: 72%;
    left: 26%;
    transform: translate(-50%, -50%);
    background-color: rgba(255, 255, 255, 0.3);
    color: white;
    border: none;
    border-radius: 4px;
    cursor: pointer;
    opacity: 0;
    width: 80px;
    height: 28px;
    font-size: 10px;
    line-height: 10px;
    font-weight: 400;
    display: flex;
    justify-content: center;
    align-items: center;
  }

  /* Show the button on hover */
  &:hover .copy-button {
    display: ${p => p?.hide ? 'none' : ''};
    opacity: 1;
  }

  .copy-button:hover {
    display: ${p => p?.hide ? 'none' : ''};
    background-color: rgba(255, 255, 255, 0.2);
  }

  /* Style for the button Article Btn */
  .add-as-hero-btn {
    display: ${p => p?.hide ? 'none' : ''};
    position: absolute;
    top: 28%;
    left: 26%;
    transform: translate(-50%, -50%);
    background-color: rgba(255, 255, 255, 0.3);
    color: white;
    border: none;
    border-radius: 4px;
    cursor: pointer;
    opacity: 0;
    width: 80px;
    height: 28px;
    font-size: 10px;
    line-height: 10px;
    font-weight: 400;
    display: flex;
    justify-content: center;
    align-items: center;
  }

  /* Black transparent overlay */
  &::before {
    display: ${p => p?.hide ? 'none' : ''};
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    border-radius: 12px;
    background-color: rgba(0, 0, 0, 0);
    transition: background-color 0.3s ease;
  }

  /* Show the button on hover */
  &:hover .add-as-hero-btn {
    display: ${p => p?.hide ? 'none' : ''};
    opacity: 1;
  }

  .add-as-hero-btn:hover {
    display: ${p => p?.hide ? 'none' : ''};
    background-color: rgba(255, 255, 255, 0.2);
  }

  &:hover::before {
    display: ${p => p?.hide ? 'none' : ''};
    background-color: rgba(0, 0, 0, 0.8);
  }

  /* Style for the button Copy Btn */
  .view-button {
    display: ${p => p?.hide ? 'none' : ''};
    position: absolute;
    top: 72%;
    left: 74%;
    transform: translate(-50%, -50%);
    background-color: rgba(255, 255, 255, 0.3);
    color: white;
    border: none;
    border-radius: 4px;
    cursor: pointer;
    opacity: 0;
    width: 80px;
    height: 28px;
    font-size: 10px;
    line-height: 10px;
    font-weight: 400;
    display: flex;
    justify-content: center;
    align-items: center;
  }

  /* Show the button on hover */
  &:hover .view-button {
    display: ${p => p?.hide ? 'none' : ''};
    opacity: 1;
  }

  .view-button:hover {
    display: ${p => p?.hide ? 'none' : ''};
    background-color: rgba(255, 255, 255, 0.2);
  }
`;
